import { type ReactNode, useEffect, useRef, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField, { type TextFieldProps } from "@mui/material/TextField";

export type ConflictValidationStatus = "idle" | "checking" | "available" | "taken";

interface IInputConflictValidatorProps {
    label: string;
    value: string;
    onValueChange: (value: string) => void;
    onCheckConflict: (value: string) => Promise<boolean | undefined>;
    conflictHelperText: string;
    onValidationStatusChange?: (status: ConflictValidationStatus) => void;
    helperText?: ReactNode;
    debounceMs?: number;
    type?: string;
    required?: boolean;
    fullWidth?: boolean;
    disabled?: boolean;
    trimValueBeforeCheck?: boolean;
    textFieldProps?: TextFieldProps;
}

function renderStatusAdornment(status: ConflictValidationStatus) {
    if (status === "checking") {
        return <CircularProgress size={18} />;
    }

    if (status === "available") {
        return <CheckCircleOutlineIcon color="success" fontSize="small" />;
    }

    if (status === "taken") {
        return <ErrorOutlineIcon color="error" fontSize="small" />;
    }

    return null;
}

export default function InputConflictValidator({
    label,
    value,
    onValueChange,
    onCheckConflict,
    conflictHelperText,
    onValidationStatusChange,
    helperText,
    debounceMs = 1000,
    type = "text",
    required = false,
    fullWidth = false,
    disabled = false,
    trimValueBeforeCheck = true,
    textFieldProps,
}: IInputConflictValidatorProps) {
    const [status, setStatus] = useState<ConflictValidationStatus>("idle");
    const requestIdRef = useRef(0);
    const onCheckConflictRef = useRef(onCheckConflict);
    const onValidationStatusChangeRef = useRef(onValidationStatusChange);

    useEffect(() => {
        onCheckConflictRef.current = onCheckConflict;
        onValidationStatusChangeRef.current = onValidationStatusChange;
    });

    useEffect(() => {
        onValidationStatusChangeRef.current?.(status);
    }, [status]);

    useEffect(() => {
        if (disabled) {
            setStatus("idle");
            return;
        }

        const nextValue = trimValueBeforeCheck ? value.trim() : value;

        if (!nextValue) {
            setStatus("idle");
            return;
        }

        const currentRequestId = requestIdRef.current + 1;
        requestIdRef.current = currentRequestId;

        setStatus("checking");

        const timer = window.setTimeout(async () => {
            const exists = await onCheckConflictRef.current(nextValue);

            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            if (exists === undefined) {
                setStatus("idle");
                return;
            }

            setStatus(exists ? "taken" : "available");
        }, debounceMs);

        return () => {
            window.clearTimeout(timer);
        };
    }, [value, disabled, debounceMs, trimValueBeforeCheck]);

    const adornment = renderStatusAdornment(status);

    return (
        <TextField
            {...textFieldProps}
            label={label}
            value={value}
            onChange={(event) => {
                onValueChange(event.target.value);
            }}
            type={type}
            required={required}
            fullWidth={fullWidth}
            disabled={disabled}
            error={status === "taken" || textFieldProps?.error === true}
            helperText={status === "taken" ? conflictHelperText : helperText}
            InputProps={{
                ...textFieldProps?.InputProps,
                endAdornment: (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        {adornment}
                        {textFieldProps?.InputProps?.endAdornment}
                    </Box>
                ),
            }}
        />
    );
}
